import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { format } from "date-fns";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
// UPPDRAG-36: clickable player names → /manager/customers/:id.
import { PlayerNameButton } from "@/components/shared/PlayerNameButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Bell, CreditCard, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PaymentStatusBadge from "../PaymentStatusBadge";
import LevelBadge from "@/components/level/LevelBadge";
import { sendTemplateEmail } from "@/lib/sendTemplateEmail";
import { useOrganization } from "@/hooks/useOrganization";
import type { Tables } from "@/integrations/supabase/types";
import type { BookingDetail, PlayerDisplayInfo, PaymentMethod } from "./types";
import { useTranslation } from "react-i18next";

interface BookingDetailPaymentsProps {
  booking: BookingDetail;
  bookingId: string;
  allPlayers: PlayerDisplayInfo[];
  /** UPPDRAG-36: callback to close the surrounding modal/sheet on navigate. */
  onAfterNavigate?: () => void;
  payments: Tables<"booking_payments">[];
  filteredPaymentMethods: PaymentMethod[];
}

export function BookingDetailPayments({
  booking,
  bookingId,
  allPlayers,
  payments,
  filteredPaymentMethods,
  onAfterNavigate,
}: BookingDetailPaymentsProps) {
  // PR-AV: useTranslation() must be called at component scope.
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  // Undo payment state (replaces native confirm() to avoid INP)
  const [undoTarget, setUndoTarget] = useState<Tables<"booking_payments"> | null>(null);
  const undoMutation = useMutation({
    mutationFn: async (payment: Tables<"booking_payments">) => {
      if ((payment as any).stripe_payment_intent_id) {
        const { error } = await supabase.functions.invoke("refund-payment", {
          body: { payment_id: payment.id, reason: "Admin ångrade betalning" },
        });
        if (error) throw error;
      } else {
        await supabase.from("booking_payments").update({
          status: "pending",
          paid_at: null,
          original_amount: payment.amount,
          adjusted_by: user?.id,
          adjusted_at: new Date().toISOString(),
          adjustment_reason: "Ångrad av admin",
        } as any).eq("id", payment.id);
      }
      // 2e-core (operator decision 4): refunding a MISMATCH-flagged row resolves the flag —
      // append a durable suffix to notes so the admin panel shows it as handled (append-safe;
      // mark-paid flows overwrite notes, appending preserves their content).
      const existingNotes = (payment as { notes?: string | null }).notes;
      if (existingNotes && existingNotes.includes("MISMATCH") && !existingNotes.includes("RESOLVED")) {
        const resolved = `${existingNotes} | RESOLVED: refunded ${new Date().toISOString().slice(0, 10)}`;
        await supabase.from("booking_payments").update({ notes: resolved } as any).eq("id", payment.id);
      }
      await supabase.from("booking_activity_log" as any).insert({
        booking_id: booking.id,
        user_id: user?.id,
        action: "payment_refunded",
        actor_type: "admin",
        description: `Betalning ångrad: ${payment.amount} kr`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking-payments-detail", bookingId] });
      qc.invalidateQueries({ queryKey: ["booking-activity-log", bookingId] });
      toast.success(t("booking:toast.paymentUndone"));
      setUndoTarget(null);
    },
    onError: (err: Error) => {
      toast.error(err.message || "Kunde inte ångra betalningen");
      setUndoTarget(null);
    },
  });

  // Mark-paid dialog state
  const [markPaymentId, setMarkPaymentId] = useState<string | null>(null);
  const [markMethod, setMarkMethod] = useState("");
  const [markReceiptNumber, setMarkReceiptNumber] = useState("");
  const [markExternalRef, setMarkExternalRef] = useState("");
  const [markNotes, setMarkNotes] = useState("");

  // Sprint D aggregates: prefer amount_minor; both totals in öre.
  const totalAmountMinor = payments.reduce(
    (sum: number, p) => sum + (resolveMoney((p as any).amount_minor, p.amount, (p as any).currency)?.valueMinor ?? 0), 0);
  const paidAmountMinor = payments
    .filter((p) => ["paid", "manual"].includes(p.status))
    .reduce((sum: number, p) => sum + (resolveMoney((p as any).amount_minor, p.amount, (p as any).currency)?.valueMinor ?? 0), 0);
  // Backwards-compat aliases for any downstream comparison expecting kronor.
  const totalAmount = Math.round(totalAmountMinor / 100);
  const paidAmount = Math.round(paidAmountMinor / 100);

  const markPaidMutation = useMutation({
    mutationFn: async () => {
      if (!markPaymentId || !markMethod || !user) throw new Error("Saknar data");
      const payment = payments.find((p) => p.id === markPaymentId);
      if (!payment) throw new Error("Betalning hittades ej");

      const methodLabel = filteredPaymentMethods.find(m => m.value === markMethod)?.label ?? markMethod;

      const { error } = await supabase
        .from("booking_payments")
        .update({
          status: "paid",
          payment_method: markMethod,
          paid_at: new Date().toISOString(),
          receipt_number: markReceiptNumber || null,
          external_reference: markExternalRef || null,
          marked_paid_by: user.id,
          notes: markNotes || null,
        } as any)
        .eq("id", markPaymentId);
      if (error) throw error;

      // Log activity
      await supabase.from("booking_activity_log" as any).insert({
        booking_id: bookingId,
        user_id: user.id,
        action: "payment_received",
        actor_type: "admin",
        description: `Betalning registrerad: ${payment.amount} kr via ${methodLabel}${markReceiptNumber ? ` (kvitto: ${markReceiptNumber})` : ""}`,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking-payments-detail", bookingId] });
      qc.invalidateQueries({ queryKey: ["booking-activity-log", bookingId] });
      toast.success(t("booking:toast.paymentRegistered"));
      closeMarkDialog();

      // Send booking confirmation email to the booking owner
      if (organization?.id && booking) {
        const start = new Date(booking.start_time);
        const end = new Date(booking.end_time);
        // Fetch the booking owner's email via a lightweight query
        supabase
          .rpc("get_user_email", { uid: booking.user_id })
          .then(({ data: email }) => {
            if (!email) return;
            // Sprint D PR-AD bug fix + Phase 4 cutover:
            // Pre-existing bug: passing "350 kr" to template body "{price} kr" rendered "350 kr kr".
            // Fix: pass plain rounded kronor number (matching send-customer-email/index.ts pattern).
            // Phase 4-aware: prefer price_total_minor (öre) over legacy price_total (kr).
            const priceMoney = resolveMoney(
              (booking as any).price_total_minor,
              booking.price_total,
              (booking as any).currency,
            );
            const priceStr = priceMoney
              ? String(Math.round(priceMoney.valueMinor / 100))
              : "–";
            sendTemplateEmail(organization.id, "booking_confirmation", email as string, {
              playerName: booking.profiles?.full_name ?? "Spelare",
              courtName: booking.courts?.name ?? "Bana",
              date: format(start, "d MMMM yyyy"),
              startTime: format(start, "HH:mm"),
              endTime: format(end, "HH:mm"),
              price: priceStr,
              clubName: organization.name,
            }).catch((err) => console.error("[ADMIN MARK PAID EMAIL ERROR]", err));
          })
          .catch(() => {});
      }
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte registrera betalning"); },
  });

  const sendReminderMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.from("notifications").insert({
        user_id: userId,
        organization_id: organization!.id,  // PR-B: org-scope per Phase 1 RLS
        type: "payment_reminder",
        title: "Betalningspåminnelse",
        body: "Du har en obetald bokning. Vänligen betala eller kontakta oss.",
        data: { booking_id: bookingId },
      });
      if (error) throw error;
    },
    onSuccess: () => toast.success(t("booking:toast.reminderSent")),
  });

  const openMarkDialog = (paymentId: string) => {
    setMarkPaymentId(paymentId);
    setMarkMethod("");
    setMarkReceiptNumber("");
    setMarkExternalRef("");
    setMarkNotes("");
  };

  const closeMarkDialog = () => {
    setMarkPaymentId(null);
    setMarkMethod("");
    setMarkReceiptNumber("");
    setMarkExternalRef("");
    setMarkNotes("");
  };

  const markDialogPayment = markPaymentId ? payments.find((p) => p.id === markPaymentId) : null;
  const markDialogPlayer = markDialogPayment ? allPlayers.find(pl => pl.id === markDialogPayment.user_id) : null;

  return (
    <>
      {/* Players */}
      <div>
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
          Spelare ({allPlayers.length})
        </h4>
        <div className="space-y-2">
          {allPlayers.map((player) => {
            const payment = payments.find((p) => p.user_id === player.id);
            const isPaid = payment && ["paid", "manual"].includes(payment.status);
            return (
              <div key={player.id} className="rounded-lg border p-2.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{(player.name ?? "?")[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-1.5">
                        {/* UPPDRAG-36: name → customer card. Falls back to a
                            plain span when the player has no customer profile
                            in this org (e.g. raw user_id from booking_players
                            with no matching customers row). */}
                        {player.customerId ? (
                          <PlayerNameButton
                            customerId={player.customerId}
                            name={player.name ?? "?"}
                            className="text-sm font-medium"
                            onAfterNavigate={onAfterNavigate}
                          />
                        ) : (
                          <span className="text-sm font-medium">{player.name}</span>
                        )}
                        {player.isBooker && <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">bokare</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {player.level && <LevelBadge level={player.level} size="sm" />}
                        {/* G-052: category badge — parity with BookingDetailPanel */}
                        {player.categoryLabel && (
                          <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5">{player.categoryLabel}</Badge>
                        )}
                        {player.checkedInAt ? (
                          <span className="text-[10px] text-green-700 flex items-center gap-0.5">
                            <CheckCircle2 className="h-3 w-3" />
                            {format(new Date(player.checkedInAt), "HH:mm")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {payment && <span className="text-xs text-muted-foreground">{formatPrice(resolveMoney((payment as any).amount_minor, payment.amount, (payment as any).currency) ?? { valueMinor: 0, currency: "SEK" })}</span>}
                    <PaymentStatusBadge status={payment?.status ?? "unpaid"} />
                  </div>
                </div>

                {/* Paid details */}
                {isPaid && payment && (
                  <div className="flex flex-wrap items-center gap-1.5 pl-10">
                    {payment.payment_method && (
                      <Badge variant="secondary" className="text-[10px] h-5">
                        {filteredPaymentMethods.find(m => m.value === payment.payment_method)?.label ?? payment.payment_method}
                      </Badge>
                    )}
                    {payment.receipt_number && (
                      <span className="text-[10px] text-muted-foreground">Kvitto: #{payment.receipt_number}</span>
                    )}
                    {payment.external_reference && (
                      <span className="text-[10px] text-muted-foreground">Ref: {payment.external_reference}</span>
                    )}
                    {/* RACE-FIX (mismatch A): notes was written but never rendered — the
                        webhook's MISMATCH flag (paid after booking cancelled) must be
                        visible to the admin, not just stored. */}
                    {payment.notes && (
                      <span className={`text-[10px] ${payment.notes.includes("MISMATCH") ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                        {payment.notes}
                      </span>
                    )}
                  </div>
                )}

                {/* Unpaid actions */}
                {isAdmin && payment && !isPaid && (
                  <div className="flex items-center gap-2 pl-10">
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => openMarkDialog(payment.id)}>
                      <CreditCard className="h-3 w-3" />
                      Markera betald
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => sendReminderMutation.mutate(player.id)}>
                      <Bell className="h-3.5 w-3.5 text-yellow-600" />
                    </Button>
                  </div>
                )}
                {/* Paid actions — undo/refund */}
                {isAdmin && payment && isPaid && (
                  <div className="flex items-center gap-2 pl-10">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1 text-destructive hover:text-destructive"
                      onClick={() => setUndoTarget(payment)}
                      disabled={undoMutation.isPending}
                    >
                      {undoMutation.isPending && undoTarget?.id === payment.id ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : null}
                      ↩️ Ångra
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoice status (for invoice-paid bookings) */}
      {isAdmin && (booking as any).invoice_status && (
        <div className="rounded-lg border p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Fakturastatus</span>
            <Select
              value={(booking as any).invoice_status}
              onValueChange={async (v) => {
                const update: Record<string, unknown> = { invoice_status: v };
                if (v === "invoiced") update.invoice_date = new Date().toISOString().slice(0, 10);
                if (v === "paid") update.invoice_paid_date = new Date().toISOString().slice(0, 10);
                update.invoice_marked_by = user?.id;
                await supabase.from("bookings").update(update as any).eq("id", booking.id);
                qc.invalidateQueries({ queryKey: ["booking-detail"] });
                qc.invalidateQueries({ queryKey: ["schedule-bookings"] });
                toast.success("Fakturastatus uppdaterad");
              }}
            >
              <SelectTrigger className="h-7 w-[160px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Att fakturera</SelectItem>
                <SelectItem value="invoiced">Fakturerad</SelectItem>
                <SelectItem value="paid">Betald</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Price summary */}
      {(totalAmount > 0 || booking.custom_price != null) && (
        <div className="rounded-lg border p-3 space-y-1.5">
          {totalAmount > 0 && (
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-muted-foreground">Betalt / Totalt</span>
              <span className="text-lg font-bold">{paidAmount} / {totalAmount} kr</span>
            </div>
          )}
          {allPlayers.length > 1 && totalAmount > 0 && (
            <p className="text-xs text-muted-foreground">
              {formatPrice(totalAmount / allPlayers.length)} per spelare
            </p>
          )}
          {booking.custom_price != null && (
            <div className="flex items-center gap-1.5 text-xs">
              <Badge variant="outline" className="text-[9px]">Anpassat pris</Badge>
              {booking.custom_price_note && (
                <span className="text-muted-foreground">{booking.custom_price_note}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Undo Payment AlertDialog (replaces native confirm for better INP) */}
      <AlertDialog open={!!undoTarget} onOpenChange={(v) => { if (!v) setUndoTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ångra betalning</AlertDialogTitle>
            <AlertDialogDescription>
              Vill du ångra betalningen på {formatPrice(resolveMoney((undoTarget as any)?.amount_minor, undoTarget?.amount, (undoTarget as any)?.currency) ?? { valueMinor: 0, currency: "SEK" })}?
              {(undoTarget as any)?.stripe_payment_intent_id ? " Kortbetalningen återbetalas via Stripe." : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoMutation.isPending}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (undoTarget) undoMutation.mutate(undoTarget); }}
              disabled={undoMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {undoMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Bearbetar...</> : "Ja, ångra"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark Paid Dialog */}
      <Dialog open={!!markPaymentId} onOpenChange={(v) => { if (!v) closeMarkDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrera betalning</DialogTitle>
          </DialogHeader>

          {markDialogPayment && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm font-medium">{markDialogPlayer?.name ?? "Spelare"}</p>
                <p className="text-lg font-bold">{formatPrice(resolveMoney((markDialogPayment as any).amount_minor, markDialogPayment.amount, (markDialogPayment as any).currency) ?? { valueMinor: 0, currency: "SEK" })}</p>
              </div>

              <div className="space-y-2">
                <Label>Betalningsmetod <span className="text-destructive">*</span></Label>
                <Select value={markMethod} onValueChange={setMarkMethod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Välj metod..." />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPaymentMethods.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Kvittonummer</Label>
                <Input
                  value={markReceiptNumber}
                  onChange={(e) => setMarkReceiptNumber(e.target.value)}
                  placeholder="Automatiskt vid kassaintegration"
                />
              </div>

              <div className="space-y-2">
                <Label>Extern referens</Label>
                <Input
                  value={markExternalRef}
                  onChange={(e) => setMarkExternalRef(e.target.value)}
                  placeholder="Swish-ref, fakturanr..."
                />
              </div>

              <div className="space-y-2">
                <Label>Anteckning</Label>
                <Input
                  value={markNotes}
                  onChange={(e) => setMarkNotes(e.target.value)}
                  placeholder="Friskvårdskort, ePassi, etc."
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeMarkDialog}>Avbryt</Button>
            <Button
              onClick={() => markPaidMutation.mutate()}
              disabled={!markMethod || markPaidMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Registrera betalning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
